import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import { App } from '../../../app';
import { ModerationContentType, ModerationListFilter, ModerationPost, ModerationStatus } from '../../../Moderation';
import * as Actions from '../../../store/actions';
import { PatchType } from '../../../store/appState';
import { isReadyToSubmit, keepFormOpen, operationKey, ticketOperationKey, UIOperationState, useOperation } from '../../../store/operation';
import { RootState } from '../../../store/rootState';
import Avatar from '../../shared/Avatar';
import Button from '../../shared/Button';
import HtmlPreview from '../../shared/HtmlPreview';

export interface PostProps {
    post: ModerationPost;
    filterChosen: ModerationListFilter;
}

const contentTypeText = (post: ModerationPost): JSX.Element => {
    switch (post.contentType) {
        case ModerationContentType.problem: return null;
        case ModerationContentType.suggestion: return null;
        case ModerationContentType.comment:
        case ModerationContentType.solution:
        case ModerationContentType.workaround: return <span>
            <a href={App.getNodeUrl(post.rootPostId, post.rootPostTitle, post.contentId, true)} target="_blank" rel="noopener noreferrer">
                <b>{post.contentType === ModerationContentType.solution ? "Solution"
                    : (post.contentType === ModerationContentType.workaround) ? "Workaround" : "Comment"}</b>
            </a>
            <span css="color: var(--color-on-surface-light)"> under </span>
        </span>;
    }
}

const getUrl = (post: ModerationPost): string => {
    switch (post.contentType) {
        case ModerationContentType.problem:
        case ModerationContentType.suggestion:
            return App.getTicketUrl(post.rootPostId, post.rootPostTitle, true);

        case ModerationContentType.comment:
        case ModerationContentType.solution:
        case ModerationContentType.workaround:
            return App.getNodeUrl(post.rootPostId, post.rootPostTitle, post.contentId, true);

        default:
            throw new Error(`ERR_UNKNOWN_POST_TYPE: Unknown post type: ${post.contentType}`);
    }
}

export default function Post({ post, filterChosen }: PostProps): JSX.Element {
    const usersUpdatedSuspensionState = useSelector((state: RootState) => state.appState.usersUpdatedSuspensionState);

    const updationOpKey = ticketOperationKey(post.contentId, PatchType.ModerationUpdatePost);
    const updationOperation = useOperation(updationOpKey);

    const authorSuspensionOpKey = operationKey(post.createdById, PatchType.UpdateUserSuspensionState);
    const authorSuspensionOperation = useOperation(authorSuspensionOpKey);

    const rejectPostClick = (): void => {
        updationOperation.resetOperation();
        Actions.moderationRejectPost(post.contentId, updationOpKey);
    };

    const publishPostClick = (): void => {
        updationOperation.resetOperation();
        Actions.moderationPublishPost(post.contentId, updationOpKey);
    };

    const suspendUserClick = (): void => {
        authorSuspensionOperation.resetOperation();
        Actions.updateUserSuspensionState(post.createdById, Actions.SuspensionActionType.Suspend, authorSuspensionOpKey);
    };

    const unsuspendUserClick = (): void => {
        authorSuspensionOperation.resetOperation();
        Actions.updateUserSuspensionState(post.createdById, Actions.SuspensionActionType.Unsuspend, authorSuspensionOpKey);
    };

    const createdByUrl = post.createdById ? App.GetUserProfileLink(post.createdById) : null;

    const userUpdatedSuspensionState = usersUpdatedSuspensionState[post.createdById];
    const isAuthorSuspended = userUpdatedSuspensionState == null || userUpdatedSuspensionState === undefined ? post.isAuthorSuspended : userUpdatedSuspensionState;

    return <Wrapper>
        <Title>
            {/* Add reason why post was flagged or rejected */}
            <InfoText>
                {post.moderationStatus === ModerationStatus.FlaggedForReview && <>Flagged by CVS, rule, or person</>}
                {post.moderationStatus === ModerationStatus.RejectedByModerator && <>Rejected by moderator</>}
                {post.moderationStatus === ModerationStatus.AcceptedByModerator && <>Accepted by moderator</>}
                {post.moderationStatus === ModerationStatus.RejectedByCVS && <>Rejected as spam by CVS</>}
                {post.moderationStatus === ModerationStatus.FlaggedByAI && <>Flagged  by AI</>}
                {post.moderationStatus === ModerationStatus.FlaggedExternalUserInInternalSpace && <>Flagged: External user posting in internal space</>}
            </InfoText>

            {contentTypeText(post)}

            <a href={getUrl(post)} target="_blank" rel="noopener noreferrer">
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: App.EscapeHtmlRawDisplay(post.rootPostTitle) }}
                />
            </a>
        </Title>
        <Content>
            <AuthorBar>
                <Avatar className='avatar-logo' name={post.createdByName} imageUrl={post.createdByImageLink} />

                <Author href={createdByUrl} target="_blank" rel="noopener noreferrer">{post.createdByName}</Author>
                {isAuthorSuspended && <span>&nbsp;<Pill>Suspended</Pill></span>}
                <span>&nbsp;-&nbsp;</span>
                <span css='font-size: 1.4rem'>Posted {post.createdDateUtc ? App.ParseDateTimeToLocal(post.createdDateUtc) : ''}&nbsp;&nbsp;&nbsp;&nbsp;</span>

                {isReadyToSubmit(authorSuspensionOperation.operation) && !isAuthorSuspended && (
                    <Button size="semiCompact" buttonType="normal" onClick={() => suspendUserClick()}>Suspend this user</Button>
                )}
                {isReadyToSubmit(authorSuspensionOperation.operation) && filterChosen === ModerationListFilter.autoRejectedPosts && isAuthorSuspended && (
                    <Button size="semiCompact" buttonType="normal" onClick={() => unsuspendUserClick()}>Unsuspend this user</Button>
                )}

                {authorSuspensionOperation.isInProgress() && <div>Suspending user...</div>}
                {authorSuspensionOperation.operation?.state === UIOperationState.Completed && <div className="info">{authorSuspensionOperation.operation.message ?? "Suspended successfully"}</div>}
                {authorSuspensionOperation.operation?.state === UIOperationState.Failed && <div className="error" css='margin-left: 1rem'>{authorSuspensionOperation.operation.message ?? "Failure while suspending"}</div>}
            </AuthorBar>

            <ReportedText>
                {post.wasPostDeletedByUser && <Pill css='margin-bottom: 1rem; margin-right: 1rem'>Post deleted by user</Pill>}
                {post.moderationStatus === ModerationStatus.AcceptedByModerator && <Pill css='margin-bottom: 1rem'>Published</Pill>}
                {post.moderationStatus === ModerationStatus.RejectedByModerator && <Pill css='margin-bottom: 1rem'>Rejected</Pill>}

                <HtmlPreview text={post.content} isPublic={true} />
            </ReportedText>
        </Content>

        <Footer>
            {!keepFormOpen(updationOperation.operation) && updationOperation.operation?.state !== UIOperationState.Completed && <>
                {post.moderationStatus !== ModerationStatus.AcceptedByModerator && filterChosen !== ModerationListFilter.recentlyCreatedPosts && <Button size="semiCompact" buttonType="normal" onClick={() => publishPostClick()}>Publish</Button>}
                {post.moderationStatus !== ModerationStatus.RejectedByModerator && filterChosen !== ModerationListFilter.autoRejectedPosts && <Button size="semiCompact" buttonType="normal" onClick={() => rejectPostClick()}>Reject</Button>}
            </>}

            {updationOperation.isInProgress() && <div>Saving...</div>}
            {updationOperation.operation?.state === UIOperationState.Failed && <div className="error">{updationOperation.operation.message ?? "Failure while saving"}</div>}
            {updationOperation.operation?.state === UIOperationState.Completed && <div className="info">{updationOperation.operation.message ?? "Saved successfully"}</div>}
        </Footer>
    </Wrapper >
}

const Wrapper = styled.div`
    border: solid .1rem var(--color-on-surface-lighter);
    border-radius: .2rem;
    background-color: var(--color-surface);

    && button {
        border-width: .1rem;
    }
`;

const Title = styled.div`
    border-bottom: solid .1rem var(--color-on-surface-lightest);
    padding: .5rem 2rem;

    >a, >span {
        display: inline;
    }
`

const Content = styled.div`
    padding: 1rem 2rem;
`

const AuthorBar = styled.div`
    && {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
        align-items: center;
    }

    .avatar-logo {
        margin-right: 1rem;
    }

    && button {
        margin-left: 2rem;
        font-size: 1.4rem;
        font-weight: normal;
        padding: .1rem .5rem;
    }
`;

const Author = styled.a`
    font-size: 1.4rem;
    color: var(--color-on-surface-link);
`;

const ReportedText = styled.div`
    padding-top: .5rem;
    font-size: 1.4rem;
    max-height: 30rem;
    overflow-y: auto;
`;

const Footer = styled.div`
    display: flex;
    padding: .8rem 2rem 1.2rem;
    border-top: solid 1px #f4f4f4;

    button {
        font-size: 1.4rem;
        font-weight: normal;
    }

    > * + * {
        margin-left: 1.5rem;
    }
`;

const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0 .5rem .1rem;
    border: solid 1px var(--color-on-surface-lighter);
    font-size: 1.2rem;
    border-radius: .8rem;
    width: fit-content;
`;

const InfoText = styled.div`
    margin: 0.5rem 0;
    font-size: 0.8em;
`;